import { useCallback, useMemo } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import {
  toCampaignAudience,
  toCampaignDeliveryBreakdown,
  toCampaignDetailHeader,
  toCampaignRailRow,
  toCampaignStep,
} from '#/entities/campaign'
import type {
  CampaignAudience,
  CampaignDeliveryBreakdown,
  CampaignDetailHeader,
  CampaignFigure,
  CampaignInsight,
  CampaignRailRow,
  CampaignReplyBucket,
  CampaignStep,
  CampaignTone,
  CampaignWaitingBand,
} from '#/entities/campaign'
import { useCampaignOverviewApiV1CampaignsCampaignIdOverviewGet } from '#/shared/api/generated/communication/theCargoCommunication'
import type {
  CampaignOverviewRead,
  CampaignStatValue,
} from '#/shared/api/generated/communication/models'
import { useDocumentTitle } from '#/shared/lib/document-title'

import {
  CAMPAIGN_DETAIL_TABS,
  CAMPAIGN_DETAIL_TAB_ORDER,
  campaignDetailTabLabelKey,
} from './detail-tabs'
import type { CampaignDetailTab } from './detail-tabs'
import { CAMPAIGN_CONTACT_FILTERS } from './contact-filters'

export interface CampaignDetailTabItem {
  key: CampaignDetailTab
  label: string
  /** Sequence and Contact carry a count beside the name. */
  count?: string
}

interface FigureSpec {
  /** Key in the service's map — snake_case, frozen in the field description. */
  apiKey: string
  /** Key of the i18n label. */
  key: string
  tone?: CampaignTone
  noteTone?: CampaignTone
}

/** Order and labels; the ink is the service's, these are the fallback. */
const PERFORMANCE_FIGURES: readonly FigureSpec[] = [
  {
    apiKey: 'waiting_on_us',
    key: 'waitingOnUs',
    tone: 'attention',
    noteTone: 'attention',
  },
  { apiKey: 'replies', key: 'replies' },
  { apiKey: 'booked_orders', key: 'bookedOrders', tone: 'ink' },
  { apiKey: 'margin_booked', key: 'marginBooked', tone: 'ink' },
  { apiKey: 'unsubscribed', key: 'unsubscribed', tone: 'paused' },
  { apiKey: 'cost_per_booked', key: 'costPerBooked', tone: 'muted' },
]

const ENGAGEMENT_FIGURES: readonly FigureSpec[] = [
  { apiKey: 'clicked', key: 'clicked', tone: 'accent' },
  // Deliberately faint: Apple Mail pre-loads images, so the number is noise.
  { apiKey: 'opened', key: 'opened', tone: 'faint' },
  { apiKey: 'quote_page', key: 'quotePage' },
  { apiKey: 'time_to_click', key: 'timeToClick', tone: 'muted' },
]

function figures(
  t: TFunction,
  section: 'performance' | 'engagement',
  specs: readonly FigureSpec[],
  values: Partial<Record<string, CampaignStatValue>> | undefined,
): CampaignFigure[] {
  if (values == null) return []
  return specs.flatMap((spec) => {
    const stat = values[spec.apiKey]
    if (stat == null) return []
    return [
      {
        key: spec.key,
        label: t(`campaigns.detail.${section}.${spec.key}`),
        value: stat.value,
        note: stat.note ?? undefined,
        // The service tones each figure; the spec is the fallback for a key
        // it has not toned.
        tone: stat.tone ?? spec.tone,
        noteTone: spec.noteTone,
      },
    ]
  })
}

export interface CampaignDetailOverview {
  waiting: CampaignWaitingBand | null
  performance: CampaignFigure[]
  performanceBasis: string
  steps: CampaignStep[]
  stepInsight: CampaignInsight | null
  engagement: CampaignFigure[]
  engagementNote: string
  emailDelivery: CampaignDeliveryBreakdown | null
  smsDelivery: CampaignDeliveryBreakdown | null
  replyBuckets: CampaignReplyBucket[]
  replyNote: string
  placement: CampaignRailRow[]
  placementNote: string
  audience: CampaignAudience | null
  cost: CampaignRailRow[]
}

type RailBlocks = Pick<
  CampaignDetailOverview,
  'placement' | 'placementNote' | 'audience' | 'cost'
>

function toRailBlocks(data: CampaignOverviewRead): RailBlocks {
  return {
    placement: data.rail.map(toCampaignRailRow),
    placementNote: data.rail_note ?? '',
    audience: data.audience ? toCampaignAudience(data.audience) : null,
    cost: (data.cost ?? []).map(toCampaignRailRow),
  }
}

/**
 * The overview blocks, renamed. Four of them are null on purpose — no waiting
 * replies, too little data for an insight, a channel that never sent — and the
 * cards hide rather than draw an empty frame.
 */
function toOverview(
  t: TFunction,
  data: CampaignOverviewRead,
): CampaignDetailOverview {
  return {
    ...toRailBlocks(data),
    waiting: data.waiting ?? null,
    performance: figures(
      t,
      'performance',
      PERFORMANCE_FIGURES,
      data.performance,
    ),
    performanceBasis: data.performance_basis,
    steps: data.steps.map(toCampaignStep),
    stepInsight: data.insight ?? null,
    engagement: figures(t, 'engagement', ENGAGEMENT_FIGURES, data.engagement),
    engagementNote: data.engagement_note ?? '',
    emailDelivery: data.email_delivery
      ? toCampaignDeliveryBreakdown(data.email_delivery)
      : null,
    smsDelivery: data.sms_delivery
      ? toCampaignDeliveryBreakdown(data.sms_delivery)
      : null,
    replyBuckets: data.reply_buckets ?? [],
    replyNote: data.reply_note ?? '',
  }
}

/**
 * Everything the campaign detail renders.
 *
 * The tab lives in the URL so a tab is linkable and survives a reload. One
 * call — `GET /campaigns/{id}/overview` — fills the header, the tab counts and
 * every Overview block; the other tabs fetch their own bodies when active.
 */
export function useCampaignDetail(campaignId: string) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Console diff: the product's route id is `/_dashboard/...`; the console's
  // layout route is `_shell`, so the same page lives at `/_shell/...`.
  const { tab, filter } = useSearch({
    from: '/_shell/campaign/$campaignId',
  })

  const query =
    useCampaignOverviewApiV1CampaignsCampaignIdOverviewGet(campaignId)
  const data = query.data?.data

  const header: CampaignDetailHeader | null = useMemo(
    () => (data?.header ? toCampaignDetailHeader(data.header) : null),
    [data],
  )
  useDocumentTitle(t('documentTitle.campaign'), header?.name)

  const onTabChange = useCallback(
    (next: CampaignDetailTab) => {
      void navigate({
        to: '/campaign/$campaignId',
        params: { campaignId },
        search: { tab: next },
        replace: true,
      })
    },
    [navigate, campaignId],
  )

  /** The waiting band's headline: the Contact tab, on the people it counts. */
  const onOpenWaiting = useCallback(() => {
    void navigate({
      to: '/campaign/$campaignId',
      params: { campaignId },
      search: {
        tab: CAMPAIGN_DETAIL_TABS.CONTACT,
        filter: CAMPAIGN_CONTACT_FILTERS.waiting,
      },
    })
  }, [navigate, campaignId])

  const tabCounts = data?.tab_counts
  const tabs: CampaignDetailTabItem[] = useMemo(
    () =>
      CAMPAIGN_DETAIL_TAB_ORDER.map((key) => ({
        key,
        label: t(campaignDetailTabLabelKey(key)),
        count:
          key === CAMPAIGN_DETAIL_TABS.SEQUENCE
            ? tabCounts?.['sequence']
            : key === CAMPAIGN_DETAIL_TABS.CONTACT
              ? tabCounts?.['contact']
              : undefined,
      })),
    [t, tabCounts],
  )

  const overview = useMemo(() => (data ? toOverview(t, data) : null), [t, data])

  return {
    campaignId,
    header,
    isPending: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
    tabs,
    activeTab: tab,
    onTabChange,
    contactFilter: filter,
    onOpenWaiting,
    overview,
  }
}

export type UseCampaignDetailResult = ReturnType<typeof useCampaignDetail>
